import type { ReferenceListsQuery } from '@revelio/data-access';

import type { GqlSelectionListKey, SelectionListState } from '../types';

export type FlatReferenceSelectionListKey = Extract<
  GqlSelectionListKey,
  | 'highest_degree'
  | 'ethnicity'
  | 'gender'
  | 'seniority'
  | 'employment_relationship'
  | 'work_schedule'
  | 'remote_type'
>;

type ReferenceListItem = {
  id: number;
  name: string;
};

const referenceListConfig = [
  ['highest_degree', 'highestDegree'],
  ['ethnicity', 'ethnicity'],
  ['gender', 'gender'],
  ['seniority', 'seniority'],
  ['employment_relationship', 'employmentRelationship'],
  ['work_schedule', 'workSchedule'],
  ['remote_type', 'remoteType'],
] as const satisfies readonly [
  FlatReferenceSelectionListKey,
  keyof ReferenceListsQuery,
][];

// Temporary (2026-09-09): remote types 2, 3 and 4 match no postings, so only 1 and 5 are offered.
const REMOTE_TYPE_ALLOWED_IDS = new Set([1, 5]);

const allowedReferenceItems = (
  selectionListKey: FlatReferenceSelectionListKey,
  items: ReferenceListItem[]
): ReferenceListItem[] =>
  selectionListKey === 'remote_type'
    ? items.filter((item) => REMOTE_TYPE_ALLOWED_IDS.has(item.id))
    : items;

const normalizeReferenceItem = (item: ReferenceListItem) => ({
  id: String(item.id),
  shortName: item.name,
  longName: item.name,
  label: item.name,
});

export const normalizeReferenceLists = (
  data: ReferenceListsQuery | null | undefined
): Partial<SelectionListState<FlatReferenceSelectionListKey>> | null => {
  if (!data) return null;

  return referenceListConfig.reduce<
    Partial<SelectionListState<FlatReferenceSelectionListKey>>
  >((acc, [selectionListKey, queryKey]) => {
    const list = data[queryKey];
    if (!Array.isArray(list)) {
      return acc;
    }
    const nonNull = list.filter(
      (item): item is ReferenceListItem => item !== null
    );
    acc[selectionListKey] = allowedReferenceItems(
      selectionListKey,
      nonNull
    ).map(normalizeReferenceItem);
    return acc;
  }, {});
};
